import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../hooks/useAuth';
import { getAccountsByType } from '../../../services/chartOfAccountsService';
import { itemsManagementService } from '../../../services/itemsManagementService';

const PurchaseFormModal = ({ isOpen, onClose, type, item, onSave, vendorOptions }) => {
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Line items state for bills
  const [lineItems, setLineItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [expenseAccountOptions, setExpenseAccountOptions] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Add new state for bill options and selected bill data
  const [billOptions, setBillOptions] = useState([]);
  const [selectedBillData, setSelectedBillData] = useState(null);
  const [billsLoading, setBillsLoading] = useState(false);

  // CRITICAL FIX: Get organization ID and user ID from authentication context
  const { user, userProfile, organizationId: authOrganizationId } = useAuth();
  const organizationId = authOrganizationId || userProfile?.organization_id || null;
  const userId = user?.id || null;

  // Fetch items and accounts for line items dropdowns (bills) and expense accounts (expenses)
  // Enhanced to also fetch bills for payment-made forms
  useEffect(() => {
    if (isOpen && organizationId) {
      if (type === 'bills' || type === 'recurring-bills') {
        fetchItemsAndAccounts();
      } else if (type === 'expenses' || type === 'recurring-expenses') {
        fetchExpenseAccounts();
      } else if (type === 'payment-made') {
        fetchAvailableBills();
      }
    }
  }, [isOpen, organizationId, type]);

  // Add new function to fetch available bills for payment
  const fetchAvailableBills = async () => {
    try {
      setBillsLoading(true);
      
      // Fetch bills that are available for payment (approved, partially_paid, overdue)
      const response = await fetch(`/api/bills?organization_id=${organizationId}&status=approved,partially_paid,overdue`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.access_token}`
        }
      });
      
      if (!response?.ok) {
        throw new Error('Failed to fetch bills');
      }
      
      const { data: bills, error: billsError } = await response?.json();
      
      if (billsError) {
        console.error('Error fetching bills:', billsError);
        setBillOptions([
          { value: '', label: 'No bills available for payment' }
        ]);
      } else {
        // Transform bills data to dropdown options format
        const billDropdownOptions = [
          { value: '', label: 'Select a bill to pay...' },
          ...(bills?.map(bill => ({
            value: bill?.id,
            label: `${bill?.bill_number} - ${bill?.vendor?.vendor_name} ($${(bill?.balance_due || bill?.total_amount)?.toLocaleString()})`,
            bill_data: {
              id: bill?.id,
              bill_number: bill?.bill_number,
              vendor_id: bill?.vendor_id,
              vendor_name: bill?.vendor?.vendor_name,
              total_amount: bill?.total_amount,
              amount_paid: bill?.amount_paid || 0,
              balance_due: bill?.balance_due || bill?.total_amount,
              due_date: bill?.due_date,
              issue_date: bill?.issue_date,
              currency_code: bill?.currency_code || 'USD',
              payment_terms: bill?.payment_terms
            }
          })) || [])
        ];
        
        console.log('Fetched bills for payment dropdown:', billDropdownOptions?.length - 1);
        setBillOptions(billDropdownOptions);
      }
    } catch (error) {
      console.error('Error fetching bills for payment:', error);
      setBillOptions([
        { value: '', label: 'Error loading bills' }
      ]);
    } finally {
      setBillsLoading(false);
    }
  };

  // Add function to handle bill selection
  const handleBillSelection = (billId) => {
    if (!billId) {
      setSelectedBillData(null);
      // Reset form fields related to bill
      setFormData(prev => ({
        ...prev,
        vendor: '',
        amount: '',
        billNumber: '',
        memo: ''
      }));
      return;
    }

    const selectedBillOption = billOptions?.find(option => option?.value === billId);
    if (selectedBillOption?.bill_data) {
      const billData = selectedBillOption?.bill_data;
      setSelectedBillData(billData);
      
      // Auto-populate form fields with bill data
      setFormData(prev => ({
        ...prev,
        vendor: billData?.vendor_id,
        amount: billData?.balance_due?.toString(),
        billNumber: billData?.bill_number,
        memo: `Payment for bill ${billData?.bill_number}`,
        bill_id: billData?.id, // Add bill_id for payment processing
        // Keep existing fields
        paymentDate: prev?.paymentDate,
        paymentMethod: prev?.paymentMethod,
        referenceNumber: prev?.referenceNumber,
        bankAccount: prev?.bankAccount,
        notes: prev?.notes
      }));
    }
  };

  const fetchItemsAndAccounts = async () => {
    try {
      setItemsLoading(true);
      
      // CRITICAL FIX: Replace mock data with actual items from Supabase
      const { data: items, error: itemsError } = await itemsManagementService?.getItems(organizationId);
      
      if (itemsError) {
        console.error('Error fetching items:', itemsError);
        // Set empty array if there's an error
        setItemOptions([
          { value: 'new-item', label: '+ Create New Item' }
        ]);
      } else {
        // Transform items data to dropdown options format
        const itemDropdownOptions = [
          { value: 'new-item', label: '+ Create New Item' },
          ...(items?.map(item => ({
            value: item?.id,
            label: `${item?.name} (${item?.sku})`,
            description: item?.description,
            purchase_price: item?.cost,
            tax_rate: item?.taxRate,
            weight_per_unit: item?.weight,
            unit_of_measure: item?.unit
          })) || [])
        ];
        
        console.log('Fetched items for dropdown:', itemDropdownOptions?.length - 1); // -1 for "Create New Item"
        setItemOptions(itemDropdownOptions);
      }
      
      // Fetch chart of accounts for expense accounts from database
      console.log('Fetching chart of accounts for organization:', organizationId);
      const { data: accounts, error: accountsError } = await getAccountsByType(
        organizationId, 
        null, // Get all account types to allow flexible selection
        true  // Only accounts that allow posting
      );
      
      if (accountsError) {
        console.error('Error fetching chart of accounts:', accountsError);
        // Fallback to sample accounts if there's an error
        setAccountOptions([
          { value: '181c1934-2163-47ab-a4a7-9699d5aabf1e', label: '1400 - Prepaid Expenses' },
          { value: '0e18e785-55d3-45f8-98c4-0295b2cb0b11', label: '3200 - Retained Earnings' }
        ]);
      } else {
        // Transform accounts data to dropdown options format
        const accountDropdownOptions = accounts?.map(account => ({
          value: account?.id,
          label: `${account?.account_code} - ${account?.account_name}`,
          account_type: account?.account_type,
          account_subtype: account?.account_subtype
        })) || [];
        
        console.log('Fetched accounts for dropdown:', accountDropdownOptions?.length);
        setAccountOptions(accountDropdownOptions);
      }
    } catch (error) {
      console.error('Error fetching items and accounts:', error);
      // Fallback to sample accounts
      setItemOptions([
        { value: 'new-item', label: '+ Create New Item' }
      ]);
      setAccountOptions([
        { value: '181c1934-2163-47ab-a4a7-9699d5aabf1e', label: '1400 - Prepaid Expenses' },
        { value: '0e18e785-55d3-45f8-98c4-0295b2cb0b11', label: '3200 - Retained Earnings' }
      ]);
    } finally {
      setItemsLoading(false);
    }
  };

  const fetchExpenseAccounts = async () => {
    try {
      console.log('Fetching expense accounts for organization:', organizationId);
      
      // Fetch expense accounts specifically for expenses
      const { data: expenseAccounts, error: expenseAccountsError } = await getAccountsByType(
        organizationId, 
        'expense', // Get expense accounts specifically
        true  // Only accounts that allow posting
      );
      
      if (expenseAccountsError) {
        console.error('Error fetching expense accounts:', expenseAccountsError);
        // Fallback to sample expense accounts if there's an error
        setExpenseAccountOptions([
          { value: 'expense-sample-1', label: '5000 - Office Supplies' },
          { value: 'expense-sample-2', label: '5001 - Software Expenses' },
          { value: 'expense-sample-3', label: '5002 - Travel Expenses' }
        ]);
      } else {
        // Transform expense accounts data to dropdown options format
        const expenseAccountDropdownOptions = expenseAccounts?.map(account => ({
          value: account?.id,
          label: `${account?.account_code} - ${account?.account_name}`,
          account_type: account?.account_type,
          account_subtype: account?.account_subtype
        })) || [];
        
        console.log('Fetched expense accounts for dropdown:', expenseAccountDropdownOptions?.length);
        setExpenseAccountOptions(expenseAccountDropdownOptions);
      }
    } catch (error) {
      console.error('Error fetching expense accounts:', error);
      // Fallback to sample expense accounts
      setExpenseAccountOptions([
        { value: 'expense-sample-1', label: '5000 - Office Supplies' },
        { value: 'expense-sample-2', label: '5001 - Software Expenses' },
        { value: 'expense-sample-3', label: '5002 - Travel Expenses' }
      ]);
    }
  };

  // Line Items Management Functions
  const addLineItem = () => {
    const newLineItem = {
      id: Date.now()?.toString(), // Temporary ID for UI
      line_number: lineItems?.length + 1,
      item_id: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 0,
      account_id: '',
      notes: '',
      weight_per_unit: 1,
      line_total: 0
    };
    setLineItems([...lineItems, newLineItem]);
  };

  const removeLineItem = (index) => {
    const updatedItems = lineItems?.filter((_, i) => i !== index);
    // Renumber the line items
    const renumberedItems = updatedItems?.map((item, i) => ({
      ...item,
      line_number: i + 1
    }));
    setLineItems(renumberedItems);
    updateBillTotals(renumberedItems);
  };

  const updateLineItem = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems?.[index],
      [field]: value
    };

    // Auto-populate item details when item is selected
    if (field === 'item_id' && value && value !== 'new-item') {
      const selectedItem = getItemDetails(value);
      if (selectedItem) {
        updatedItems[index] = {
          ...updatedItems?.[index],
          item_description: selectedItem?.description || selectedItem?.label?.split(' (')?.[0],
          unit_price: selectedItem?.purchase_price || 0,
          tax_rate: selectedItem?.tax_rate || 0,
          weight_per_unit: selectedItem?.weight_per_unit || 1
        };
      }
    }

    // Calculate line total when quantity or unit price changes
    if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
      const quantity = parseFloat(updatedItems?.[index]?.quantity) || 0;
      const unitPrice = parseFloat(updatedItems?.[index]?.unit_price) || 0;
      const taxRate = parseFloat(updatedItems?.[index]?.tax_rate) || 0;
      
      const subtotal = quantity * unitPrice;
      const taxAmount = (subtotal * taxRate) / 100;
      updatedItems[index].line_total = subtotal + taxAmount;
    }

    setLineItems(updatedItems);
    updateBillTotals(updatedItems);
  };

  const getItemDetails = (itemId) => {
    // CRITICAL FIX: Replace mock data with actual item from itemOptions
    const selectedItem = itemOptions?.find(item => item?.value === itemId);
    if (selectedItem && selectedItem?.value !== 'new-item') {
      return {
        label: selectedItem?.label,
        description: selectedItem?.description,
        purchase_price: selectedItem?.purchase_price,
        tax_rate: selectedItem?.tax_rate,
        weight_per_unit: selectedItem?.weight_per_unit
      };
    }
    return null;
  };

  const updateBillTotals = (items) => {
    const subtotal = items?.reduce((sum, item) => {
      const quantity = parseFloat(item?.quantity) || 0;
      const unitPrice = parseFloat(item?.unit_price) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const taxAmount = items?.reduce((sum, item) => {
      const quantity = parseFloat(item?.quantity) || 0;
      const unitPrice = parseFloat(item?.unit_price) || 0;
      const taxRate = parseFloat(item?.tax_rate) || 0;
      const lineSubtotal = quantity * unitPrice;
      return sum + ((lineSubtotal * taxRate) / 100);
    }, 0);

    const totalAmount = subtotal + taxAmount;

    setFormData(prev => ({
      ...prev,
      subtotal: subtotal?.toFixed(2),
      tax_amount: taxAmount?.toFixed(2),
      total_amount: totalAmount?.toFixed(2)
    }));
  };

  // Define form configurations for each purchase document type
  const getFormConfig = () => {
    const configs = {
      vendors: {
        title: item ? 'Edit Vendor' : 'Add New Vendor',
        tabs: [
          { id: 'basic', label: 'Basic Info', icon: 'Building2' },
          { id: 'contact', label: 'Contact Details', icon: 'Mail' },
          { id: 'payment', label: 'Payment Terms', icon: 'CreditCard' }
        ],
        fields: {
          vendor_name: { required: true, label: 'Vendor Name', type: 'text' },
          email: { required: true, label: 'Email Address', type: 'email' },
          phone: { required: false, label: 'Phone Number', type: 'tel' },
          company_name: { required: false, label: 'Company Name', type: 'text' },
          website: { required: false, label: 'Website', type: 'url' },
          address_line_1: { required: false, label: 'Address Line 1', type: 'text' },
          address_line_2: { required: false, label: 'Address Line 2', type: 'text' },
          city: { required: false, label: 'City', type: 'text' },
          state_province: { required: false, label: 'State/Province', type: 'text' },
          postal_code: { required: false, label: 'ZIP/Postal Code', type: 'text' },
          country: { required: false, label: 'Country', type: 'text' },
          tax_id: { required: false, label: 'Tax ID', type: 'text' },
          payment_terms: { required: false, label: 'Payment Terms', type: 'select', options: [
            { value: 'net_15', label: 'Net 15' },
            { value: 'net_30', label: 'Net 30' },
            { value: 'net_45', label: 'Net 45' },
            { value: 'net_60', label: 'Net 60' },
            { value: 'due_on_receipt', label: 'Due on Receipt' },
            { value: 'advance_payment', label: 'Advance Payment' },
            { value: 'cash_on_delivery', label: 'Cash on Delivery' }
          ]},
          credit_limit: { required: false, label: 'Credit Limit', type: 'number' },
          currency_code: { required: false, label: 'Currency Code', type: 'text' },
          status: { required: false, label: 'Status', type: 'select', options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'archived', label: 'Archived' }
          ]},
          notes: { required: false, label: 'Notes', type: 'textarea' }
        }
      },
      expenses: {
        title: item ? 'Edit Expense' : 'Record New Expense',
        tabs: [
          { id: 'basic', label: 'Expense Details', icon: 'Receipt' },
          { id: 'categorization', label: 'Account & Tax', icon: 'Tag' },
          { id: 'receipt', label: 'Receipt & Notes', icon: 'FileText' }
        ],
        fields: {
          vendor: { required: false, label: 'Vendor', type: 'select', options: vendorOptions },
          description: { required: true, label: 'Description', type: 'text' },
          amount: { required: true, label: 'Amount', type: 'number' },
          expenseDate: { required: true, label: 'Expense Date', type: 'date' },
          account_id: { required: true, label: 'Account', type: 'select', options: expenseAccountOptions },
          paymentMethod: { required: true, label: 'Payment Method', type: 'select', options: [
            { value: 'cash', label: 'Cash' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'debit_card', label: 'Debit Card' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'check', label: 'Check' }
          ]},
          taxAmount: { required: false, label: 'Tax Amount', type: 'number' },
          reference: { required: false, label: 'Reference Number', type: 'text' },
          billable: { required: false, label: 'Billable to Customer', type: 'checkbox' },
          receiptAttached: { required: false, label: 'Receipt Attached', type: 'checkbox' },
          notes: { required: false, label: 'Notes', type: 'textarea' }
        }
      },
      'recurring-expenses': {
        title: item ? 'Edit Recurring Expense' : 'Setup Recurring Expense',
        tabs: [
          { id: 'basic', label: 'Expense Details', icon: 'RefreshCw' },
          { id: 'schedule', label: 'Recurring Schedule', icon: 'Calendar' },
          { id: 'settings', label: 'Settings & Options', icon: 'Settings' }
        ],
        fields: {
          template_name: { required: true, label: 'Template Name', type: 'text' },
          vendor_id: { required: false, label: 'Vendor', type: 'select', options: vendorOptions },
          description: { required: true, label: 'Description', type: 'text' },
          amount: { required: true, label: 'Amount', type: 'number' },
          account_id: { required: true, label: 'Account', type: 'select', options: expenseAccountOptions },
          recurring_frequency: { required: true, label: 'Frequency', type: 'select', options: [
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'yearly', label: 'Yearly' }
          ]},
          start_date: { required: true, label: 'Start Date', type: 'date' },
          end_date: { required: false, label: 'End Date (Optional)', type: 'date' },
          next_expense_date: { required: true, label: 'Next Expense Date', type: 'date' },
          payment_method: { required: true, label: 'Payment Method', type: 'select', options: [
            { value: 'cash', label: 'Cash' },
            { value: 'check', label: 'Check' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'debit_card', label: 'Debit Card' },
            { value: 'online', label: 'Online Payment' },
            { value: 'upi', label: 'UPI' },
            { value: 'wallet', label: 'Wallet' }
          ]},
          tax_rate: { required: false, label: 'Tax Rate (%)', type: 'number' },
          tax_amount: { required: false, label: 'Tax Amount', type: 'number' },
          currency_code: { required: false, label: 'Currency Code', type: 'text' },
          exchange_rate: { required: false, label: 'Exchange Rate', type: 'number' },
          auto_approve: { required: false, label: 'Auto-approve Expenses', type: 'checkbox' },
          is_billable: { required: false, label: 'Billable to Customer', type: 'checkbox' },
          send_notification: { required: false, label: 'Send Notifications', type: 'checkbox' },
          reference_number: { required: false, label: 'Reference Number', type: 'text' },
          notes: { required: false, label: 'Notes', type: 'textarea' }
        }
      },
      bills: {
        title: item ? 'Edit Bill' : 'Create New Bill',
        tabs: [
          { id: 'basic', label: 'Bill Details', icon: 'FileText' },
          { id: 'items', label: 'Line Items', icon: 'Package' },
          { id: 'payment', label: 'Payment Terms', icon: 'CreditCard' }
        ],
        fields: {
          vendor_id: { required: true, label: 'Vendor', type: 'select', options: vendorOptions },
          bill_number: { required: true, label: 'Bill Number', type: 'text' },
          issue_date: { required: true, label: 'Issue Date', type: 'date' },
          due_date: { required: true, label: 'Due Date', type: 'date' },
          status: { required: false, label: 'Status', type: 'select', options: [
            { value: 'draft', label: 'Draft' },
            { value: 'pending_approval', label: 'Pending Approval' },
            { value: 'approved', label: 'Approved' },
            { value: 'paid', label: 'Paid' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'rejected', label: 'Rejected' }
          ]},
          reference_number: { required: false, label: 'Vendor Reference', type: 'text' },
          subtotal: { required: false, label: 'Subtotal', type: 'number', readonly: true },
          tax_amount: { required: false, label: 'Tax Amount', type: 'number', readonly: true },
          total_amount: { required: true, label: 'Total Amount', type: 'number', readonly: true },
          payment_terms: { required: false, label: 'Payment Terms', type: 'select', options: [
            { value: 'net_15', label: 'Net 15' },
            { value: 'net_30', label: 'Net 30' },
            { value: 'net_45', label: 'Net 45' },
            { value: 'net_60', label: 'Net 60' },
            { value: 'due_on_receipt', label: 'Due on Receipt' },
            { value: 'advance_payment', label: 'Advance Payment' }
          ]},
          notes: { required: false, label: 'Notes', type: 'textarea' }
        }
      },
      'recurring-bills': {
        title: item ? 'Edit Recurring Bill' : 'Setup Recurring Bill',
        tabs: [
          { id: 'basic', label: 'Bill Details', icon: 'RotateCcw' },
          { id: 'schedule', label: 'Recurring Schedule', icon: 'Calendar' }
        ],
        fields: {
          vendor_id: { required: true, label: 'Vendor', type: 'select', options: vendorOptions },
          bill_description: { required: true, label: 'Bill Description', type: 'text' },
          total_amount: { required: true, label: 'Amount', type: 'number' },
          recurring_frequency: { required: true, label: 'Frequency', type: 'select', options: [
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'yearly', label: 'Yearly' }
          ]},
          issue_date: { required: true, label: 'First Issue Date', type: 'date' },
          due_date: { required: true, label: 'Due Date (from issue)', type: 'date' },
          next_billing_date: { required: true, label: 'Next Bill Date', type: 'date' },
          recurring_end_date: { required: false, label: 'End Date (Optional)', type: 'date' },
          payment_terms: { required: false, label: 'Payment Terms', type: 'select', options: [
            { value: 'net_15', label: 'Net 15' },
            { value: 'net_30', label: 'Net 30' },
            { value: 'net_45', label: 'Net 45' },
            { value: 'net_60', label: 'Net 60' },
            { value: 'due_on_receipt', label: 'Due on Receipt' }
          ]},
          reference_number: { required: false, label: 'Reference Number', type: 'text' },
          subtotal: { required: false, label: 'Subtotal', type: 'number' },
          tax_amount: { required: false, label: 'Tax Amount', type: 'number' },
          notes: { required: false, label: 'Notes', type: 'textarea' }
        }
      },
      'payment-made': {
        title: item ? 'Edit Payment Record' : 'Record Payment Made',
        tabs: [
          { id: 'basic', label: 'Payment Details', icon: 'CreditCard' },
          { id: 'allocation', label: 'Bill Allocation', icon: 'DollarSign' }
        ],
        fields: {
          bill_selection: { required: false, label: 'Select Bill to Pay', type: 'select', options: billOptions },
          vendor: { required: true, label: 'Vendor', type: 'select', options: vendorOptions },
          amount: { required: true, label: 'Payment Amount', type: 'number' },
          paymentDate: { required: true, label: 'Payment Date', type: 'date' },
          paymentMethod: { required: true, label: 'Payment Method', type: 'select', options: [
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'check', label: 'Check' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'debit_card', label: 'Debit Card' },
            { value: 'cash', label: 'Cash' },
            { value: 'online', label: 'Online Payment' },
            { value: 'upi', label: 'UPI' },
            { value: 'wallet', label: 'Wallet' }
          ]},
          referenceNumber: { required: false, label: 'Reference Number', type: 'text' },
          billNumber: { required: false, label: 'Bill Number', type: 'text', readonly: true },
          bankAccount: { required: false, label: 'Bank Account', type: 'select', options: [
            { value: 'checking-001', label: 'Business Checking - 001' },
            { value: 'savings-002', label: 'Business Savings - 002' },
            { value: 'credit-003', label: 'Business Credit - 003' }
          ]},
          memo: { required: false, label: 'Memo/Description', type: 'text' },
          notes: { required: false, label: 'Internal Notes', type: 'textarea' }
        }
      },
      'vendor-credit': {
        title: item ? 'Edit Vendor Credit' : 'Process Vendor Credit',
        tabs: [
          { id: 'basic', label: 'Credit Details', icon: 'Banknote' },
          { id: 'reason', label: 'Reason & Application', icon: 'MessageSquare' }
        ],
        fields: {
          vendor: { required: true, label: 'Vendor', type: 'select', options: vendorOptions },
          creditNumber: { required: true, label: 'Credit Number', type: 'text' },
          creditDate: { required: true, label: 'Credit Date', type: 'date' },
          amount: { required: true, label: 'Credit Amount', type: 'number' },
          reason: { required: true, label: 'Reason for Credit', type: 'select', options: [
            { value: 'vendor_refund', label: 'Vendor Refund' },
            { value: 'pricing_adjustment', label: 'Pricing Adjustment' },
            { value: 'service_issue', label: 'Service Issue' },
            { value: 'overpayment', label: 'Overpayment Refund' },
            { value: 'discount', label: 'Discount' },
            { value: 'other', label: 'Other' }
          ]},
          referenceBill: { required: false, label: 'Reference Bill', type: 'text' },
          referenceNumber: { required: false, label: 'Reference Number', type: 'text' },
          applyTo: { required: false, label: 'Apply Credit To', type: 'select', options: [
            { value: 'future-bills', label: 'Future Bills' },
            { value: 'specific-bill', label: 'Specific Bill' },
            { value: 'refund-request', label: 'Request Refund' }
          ]},
          description: { required: false, label: 'Description', type: 'textarea' },
          notes: { required: false, label: 'Internal Notes', type: 'textarea' }
        }
      }
    };
    return configs?.[type] || configs?.vendors;
  };

  const config = getFormConfig();

  // Initialize form data based on document type
  useEffect(() => {
    if (isOpen) {
      const defaultData = {};
      Object.keys(config?.fields)?.forEach(field => {
        const fieldConfig = config?.fields?.[field];
        if (fieldConfig?.type === 'checkbox') {
          defaultData[field] = false;
        } else if (fieldConfig?.type === 'number') {
          defaultData[field] = '';
        } else if (fieldConfig?.type === 'date') {
          if (field === 'issue_date' || field === 'paymentDate' || field === 'expenseDate' || field === 'creditDate' || field === 'expense_date' || field === 'credit_date') {
            defaultData[field] = new Date()?.toISOString()?.split('T')?.[0];
          } else if (field === 'startDate' || field === 'start_date') {
            defaultData[field] = new Date()?.toISOString()?.split('T')?.[0];
          } else if (field === 'due_date' || field === 'next_billing_date' || field === 'next_expense_date') {
            // Set due dates to 30 days from now by default
            const nextMonth = new Date();
            nextMonth?.setDate(nextMonth?.getDate() + 30);
            defaultData[field] = nextMonth?.toISOString()?.split('T')?.[0];
          } else {
            defaultData[field] = '';
          }
        } else {
          defaultData[field] = '';
        }
      });

      // Set defaults for different document types
      if (type === 'vendors') {
        defaultData.country = 'United States';
        defaultData.currency_code = 'USD';
        defaultData.payment_terms = 'net_30';
        defaultData.status = 'active';
        defaultData.credit_limit = '0.00';
      } else if (type === 'expenses') {
        defaultData.expense_number = `EXP-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        defaultData.payment_method = 'credit_card';
        defaultData.status = 'approved'; // CONFIRMED: Open status for expenses
        // Remove hardcoded category - will use account_id instead
      } else if (type === 'recurring-expenses') {
        defaultData.template_name = `Recurring Expense ${String(Date.now())?.slice(-6)}`;
        defaultData.template_number = `REC-EXP-${String(Date.now())?.slice(-4)}`;
        defaultData.payment_method = 'bank_transfer';
        defaultData.recurring_frequency = 'monthly';
        defaultData.status = 'active'; // CONFIRMED: Open/Active status for recurring expenses
        defaultData.currency_code = 'USD';
        defaultData.exchange_rate = '1.0000';
        defaultData.tax_rate = '0.00';
        defaultData.tax_amount = '0.00';
        defaultData.auto_approve = false;
        defaultData.is_billable = false;
        defaultData.send_notification = true;
        defaultData.is_active = true;
        
        // Set next expense date based on frequency
        const nextExpense = new Date();
        nextExpense?.setMonth(nextExpense?.getMonth() + 1);
        defaultData.next_expense_date = nextExpense?.toISOString()?.split('T')?.[0];
        // Remove hardcoded category - will use account_id instead
      } else if (type === 'bills') {
        // CONFIRMED: Allow manual bill number entry - generate suggested number but allow override
        defaultData.bill_number = `BILL-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        defaultData.payment_terms = 'net_30';
        defaultData.status = 'approved'; // CONFIRMED: Open status for bills
        defaultData.currency_code = 'USD';
        defaultData.subtotal = '0.00';
        defaultData.tax_amount = '0.00';
        defaultData.total_amount = '0.00';
        
        // Initialize with one empty line item
        setLineItems([{
          id: Date.now()?.toString(),
          line_number: 1,
          item_id: '',
          item_description: '',
          quantity: 1,
          unit_price: 0,
          tax_rate: 0,
          account_id: '',
          notes: '',
          weight_per_unit: 1,
          line_total: 0
        }]);
      } else if (type === 'recurring-bills') {
        // Auto-generate a bill number for recurring bills
        defaultData.bill_number = `REC-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        defaultData.recurring_frequency = 'monthly';
        defaultData.payment_terms = 'net_30';
        defaultData.status = 'approved'; // CONFIRMED: Open status for recurring bills
        defaultData.currency_code = 'USD';
        defaultData.is_recurring = true;
        
        const nextMonth = new Date();
        nextMonth?.setMonth(nextMonth?.getMonth() + 1);
        defaultData.next_billing_date = nextMonth?.toISOString()?.split('T')?.[0];
      } else if (type === 'payment-made') {
        defaultData.paymentNumber = `PAY-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        defaultData.payment_method = 'bank_transfer';
        defaultData.currency_code = 'USD';
        defaultData.status = 'cleared'; // CONFIRMED: Open status for payments made
      } else if (type === 'vendor-credit') {
        defaultData.creditNumber = `VC-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        defaultData.reason = 'vendor_refund';
        defaultData.applied_to_type = 'future_bills';
        defaultData.status = 'issued'; // CONFIRMED: Open/Sent status for vendor credits
        defaultData.currency_code = 'USD';
      }

      if (item) {
        setFormData({ ...defaultData, ...item });
        // Load existing line items if editing a bill
        if ((type === 'bills' || type === 'recurring-bills') && item?.line_items) {
          setLineItems(item?.line_items);
        }
      } else {
        setFormData(defaultData);
      }
      
      setErrors({});
      setActiveTab('basic');
    }
  }, [isOpen, type, item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Enhanced validation for bills specifically
    if (type === 'bills') {
      // Check required fields for bills
      if (!formData?.vendor_id) {
        newErrors.vendor_id = 'Please select a vendor';
      }
      if (!formData?.bill_number?.trim()) {
        newErrors.bill_number = 'Bill number is required';
      }
      if (!formData?.issue_date) {
        newErrors.issue_date = 'Issue date is required';
      }
      if (!formData?.due_date) {
        newErrors.due_date = 'Due date is required';
      }
      
      // Validate line items
      if (!lineItems || lineItems?.length === 0) {
        newErrors.lineItems = 'At least one line item is required';
      } else {
        lineItems?.forEach((item, index) => {
          if (!item?.item_description) {
            newErrors[`lineItem_${index}_description`] = 'Description is required';
          }
          if (!item?.quantity || parseFloat(item?.quantity) <= 0) {
            newErrors[`lineItem_${index}_quantity`] = 'Quantity must be greater than 0';
          }
          if (!item?.unit_price || parseFloat(item?.unit_price) < 0) {
            newErrors[`lineItem_${index}_unit_price`] = 'Unit price must be 0 or greater';
          }
          if (!item?.account_id) {
            newErrors[`lineItem_${index}_account`] = 'Please select an account';
          }
        });
      }
      
      // Validate date logic
      if (formData?.issue_date && formData?.due_date) {
        const issueDate = new Date(formData?.issue_date);
        const dueDate = new Date(formData?.due_date);
        
        if (dueDate <= issueDate) {
          newErrors.due_date = 'Due date must be after issue date';
        }
      }
      
      // Validate tax calculations if provided
      if (formData?.tax_amount && parseFloat(formData?.tax_amount) < 0) {
        newErrors.tax_amount = 'Tax amount cannot be negative';
      }
    } else {
      // Original validation for other types
      Object.keys(config?.fields)?.forEach(field => {
        const fieldConfig = config?.fields?.[field];
        if (fieldConfig?.required && !formData?.[field]) {
          newErrors[field] = `${fieldConfig?.label} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    // Show loading state
    setLoading(true);
    
    try {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      let processedData = {
        ...formData,
        type: type,
        status: formData?.status || (type === 'vendors' ? 'active' : getDefaultStatusForType(type)) // UPDATED: Use helper function for consistent default statuses
      };
      
      // Add helper function for consistent status defaults
      function getDefaultStatusForType(documentType) {
        switch (documentType) {
          case 'bills': case'recurring-bills':
            return 'approved'; // Open status for bills
          case 'expenses': case'recurring-expenses':
            return 'approved'; // Open status for expenses  
          case 'payment-made':
            return 'cleared'; // Open status for payments
          case 'vendor-credit':
            return 'issued'; // Open/Sent status for credits
          case 'vendors':
            return 'active';
          default:
            return 'approved'; // Default to Open status
        }
      }
      
      // Enhanced processing for bills
      if (type === 'bills') {
        // Ensure proper data mapping for bills
        processedData = {
          vendor_id: processedData?.vendor_id,
          bill_number: processedData?.bill_number?.trim(), // ENSURE bill_number is properly passed
          issue_date: processedData?.issue_date,
          due_date: processedData?.due_date,
          reference_number: processedData?.reference_number || null,
          subtotal: processedData?.subtotal ? parseFloat(processedData?.subtotal) : null,
          tax_amount: processedData?.tax_amount ? parseFloat(processedData?.tax_amount) : null,
          total_amount: parseFloat(processedData?.total_amount) || 0,
          currency_code: processedData?.currency_code || 'USD',
          payment_terms: processedData?.payment_terms || 'net_30',
          notes: processedData?.notes || null,
          status: processedData?.status || 'draft', // Preserve existing status for edits
          customer_id: processedData?.customer_id || null,
          // Add organization and user context
          organization_id: organizationId,
          created_by: userId,
          // Add line items
          line_items: lineItems?.map(item => ({
            item_id: item?.item_id || null,
            item_description: item?.item_description,
            quantity: parseFloat(item?.quantity) || 0,
            unit_price: parseFloat(item?.unit_price) || 0,
            tax_rate: parseFloat(item?.tax_rate) || 0,
            account_id: item?.account_id,
            notes: item?.notes || null,
            weight_per_unit: parseFloat(item?.weight_per_unit) || 1,
            line_number: item?.line_number
          }))
        };
        
        // ENHANCED: If editing existing bill, preserve the existing ID
        if (item?.id) {
          processedData.id = item?.id;
        }
      } else if (type === 'recurring-bills') {
        // Enhanced recurring bills processing using bills table structure
        processedData = {
          vendor_id: processedData?.vendor_id,
          bill_number: processedData?.bill_number || `REC-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`,
          issue_date: processedData?.issue_date,
          due_date: processedData?.due_date,
          reference_number: processedData?.reference_number || null,
          subtotal: processedData?.subtotal ? parseFloat(processedData?.subtotal) : null,
          tax_amount: processedData?.tax_amount ? parseFloat(processedData?.tax_amount) : null,
          total_amount: parseFloat(processedData?.total_amount) || 0,
          currency_code: processedData?.currency_code || 'USD',
          payment_terms: processedData?.payment_terms || 'net_30',
          notes: processedData?.notes || null,
          status: processedData?.status || 'draft',
          
          // Recurring bill specific fields
          is_recurring: true,
          recurring_frequency: processedData?.recurring_frequency || 'monthly',
          next_billing_date: processedData?.next_billing_date,
          recurring_end_date: processedData?.recurring_end_date || null,
          
          // Organization and user context
          organization_id: organizationId,
          created_by: userId
        };
        
        // Validate recurring bill specific fields
        if (!processedData?.vendor_id) {
          throw new Error('Please select a vendor for the recurring bill');
        }
        
        if (!processedData?.recurring_frequency) {
          throw new Error('Please select a recurring frequency');
        }
        
        if (!processedData?.next_billing_date) {
          throw new Error('Please specify the next billing date');
        }
        
        // Validate date logic for recurring bills
        if (processedData?.issue_date && processedData?.due_date) {
          const issueDate = new Date(processedData?.issue_date);
          const dueDate = new Date(processedData?.due_date);
          
          if (dueDate <= issueDate) {
            throw new Error('Due date must be after issue date');
          }
        }
        
        if (processedData?.next_billing_date && processedData?.issue_date) {
          const nextBillingDate = new Date(processedData?.next_billing_date);
          const issueDate = new Date(processedData?.issue_date);
          
          if (nextBillingDate <= issueDate) {
            throw new Error('Next billing date should be after the first issue date');
          }
        }
        
        // Validate end date if provided
        if (processedData?.recurring_end_date && processedData?.next_billing_date) {
          const endDate = new Date(processedData?.recurring_end_date);
          const nextBillingDate = new Date(processedData?.next_billing_date);
          
          if (endDate <= nextBillingDate) {
            throw new Error('End date must be after the next billing date');
          }
        }
        
        // Preserve existing ID if editing
        if (item?.id) {
          processedData.id = item?.id;
        }
      } else {
        // Original processing for other document types
        // Convert numeric fields appropriately for different types
        if (type === 'vendors' && processedData?.credit_limit) {
          processedData.credit_limit = parseFloat(processedData?.credit_limit) || 0;
        } else if (type === 'expenses' || type === 'recurring-expenses') {
          // CRITICAL FIX: Properly extract vendor UUID from vendor object
          if (processedData?.vendor) {
            // Check if vendor is an object with id property or just a string UUID
            if (typeof processedData?.vendor === 'object' && processedData?.vendor?.id) {
              processedData.vendor_id = processedData?.vendor?.id;
            } else if (typeof processedData?.vendor === 'string') {
              processedData.vendor_id = processedData?.vendor;
            }
            delete processedData?.vendor; // Remove the original vendor field
          }
          
          // CRITICAL FIX: Proper field mapping for expenses
          if (processedData?.expenseDate) {
            processedData.expense_date = processedData?.expenseDate;
            delete processedData?.expenseDate;
          }
          
          if (processedData?.paymentMethod) {
            processedData.payment_method = processedData?.paymentMethod;
            delete processedData?.paymentMethod;
          }
          
          if (processedData?.taxAmount) {
            processedData.tax_amount = parseFloat(processedData?.taxAmount) || 0;
            delete processedData?.taxAmount;
          }
          
          if (processedData?.reference) {
            processedData.reference_number = processedData?.reference;
            delete processedData?.reference;
          }
          
          if (processedData?.billable !== undefined) {
            processedData.is_billable = processedData?.billable;
            delete processedData?.billable;
          }
          
          if (processedData?.receiptAttached !== undefined) {
            processedData.receipt_attached = processedData?.receiptAttached;
            delete processedData?.receiptAttached;
          }
          
          // Convert numeric fields
          if (processedData?.amount) {
            processedData.amount = parseFloat(processedData?.amount) || 0;
          }
          if (processedData?.tax_amount) {
            processedData.tax_amount = parseFloat(processedData?.tax_amount) || 0;
          }
          if (processedData?.tax_rate) {
            processedData.tax_rate = parseFloat(processedData?.tax_rate) || 0;
          }
          if (processedData?.exchange_rate) {
            processedData.exchange_rate = parseFloat(processedData?.exchange_rate) || 1.0000;
          }
          
          // Set default values and add organization context
          processedData.organization_id = organizationId;
          processedData.created_by = userId;
          processedData.currency_code = processedData?.currency_code || 'USD';
          processedData.exchange_rate = parseFloat(processedData?.exchange_rate) || 1.0000;
          
          // Handle recurring-expenses specific field mapping
          if (type === 'recurring-expenses') {
            // Map frontend field names to database column names
            if (processedData?.vendor && !processedData?.vendor_id) {
              // Handle vendor mapping for recurring expenses too
              if (typeof processedData?.vendor === 'object' && processedData?.vendor?.id) {
                processedData.vendor_id = processedData?.vendor?.id;
              } else if (typeof processedData?.vendor === 'string') {
                processedData.vendor_id = processedData?.vendor;
              }
              delete processedData?.vendor;
            }
            
            // Ensure all boolean fields are properly set
            processedData.auto_approve = processedData?.auto_approve || false;
            processedData.is_billable = processedData?.is_billable || false;
            processedData.send_notification = processedData?.send_notification !== false; // Default to true
            processedData.is_active = processedData?.is_active !== false; // Default to true
            
            // Set currency defaults
            processedData.currency_code = processedData?.currency_code || 'USD';
            processedData.exchange_rate = parseFloat(processedData?.exchange_rate) || 1.0000;
            
            // Calculate next expense date if not provided
            if (!processedData?.next_expense_date && processedData?.start_date && processedData?.recurring_frequency) {
              const startDate = new Date(processedData?.start_date);
              const nextDate = new Date(startDate);
              
              switch (processedData?.recurring_frequency) {
                case 'weekly':
                  nextDate?.setDate(nextDate?.getDate() + 7);
                  break;
                case 'monthly':
                  nextDate?.setMonth(nextDate?.getMonth() + 1);
                  break;
                case 'quarterly':
                  nextDate?.setMonth(nextDate?.getMonth() + 3);
                  break;
                case 'yearly':
                  nextDate?.setFullYear(nextDate?.getFullYear() + 1);
                  break;
                default:
                  nextDate?.setMonth(nextDate?.getMonth() + 1);
              }
              
              processedData.next_expense_date = nextDate?.toISOString()?.split('T')?.[0];
            }
          }
        } else if (type === 'recurring-bills') {
          // CRITICAL FIX: Configure recurring bills to use bills table approach with vendor relationship
          if (processedData?.bill_description) {
            processedData.bill_description = processedData?.bill_description;
            delete processedData?.bill_description;
          }
          
          // Map to bills table structure for recurring bill functionality
          processedData.is_recurring = true; // Mark as recurring bill
          processedData.vendor_id = processedData?.vendor_id; // Use vendor, not customer
          processedData.recurring_frequency = processedData?.recurring_frequency || 'monthly';
          processedData.issue_date = processedData?.issue_date || new Date()?.toISOString()?.split('T')?.[0];
          processedData.due_date = processedData?.due_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
          processedData.next_billing_date = processedData?.next_billing_date;
          processedData.recurring_end_date = processedData?.recurring_end_date || null;
          processedData.total_amount = parseFloat(processedData?.total_amount) || 0;
          processedData.payment_terms = processedData?.payment_terms || 'net_30';
          processedData.currency_code = processedData?.currency_code || 'USD';
          processedData.status = processedData?.status || 'draft';
          processedData.notes = processedData?.notes || null;
          
          // Add organization context
          processedData.organization_id = organizationId;
          processedData.created_by = userId;
          
          // Clean up any fields that don't belong in bills table
          delete processedData?.customer;
          delete processedData?.customer_id;
          delete processedData?.frequency;
          delete processedData?.startDate;
          delete processedData?.endDate;
          delete processedData?.invoice_template;
          delete processedData?.templateName;
          delete processedData?.category; // Category not in bills table
          delete processedData?.autoApprove;
          delete processedData?.sendNotification;
          delete processedData?.amount;
        } else if (type === 'payment-made') {
          // CRITICAL FIX: Proper vendor UUID extraction for payments
          if (processedData?.vendor) {
            if (typeof processedData?.vendor === 'object' && processedData?.vendor?.id) {
              processedData.vendor_id = processedData?.vendor?.id;
            } else if (typeof processedData?.vendor === 'string') {
              processedData.vendor_id = processedData?.vendor;
            }
            delete processedData?.vendor;
          }
          
          if (processedData?.amount) {
            processedData.amount_paid = parseFloat(processedData?.amount) || 0;
            delete processedData?.amount; // Use amount_paid for payments_made table
          }
          if (processedData?.paymentDate) {
            processedData.payment_date = processedData?.paymentDate;
            delete processedData?.paymentDate;
          }
          if (processedData?.referenceNumber) {
            processedData.reference_number = processedData?.referenceNumber;
            delete processedData?.referenceNumber;
          }
          // CRITICAL FIX: Map bankAccount field to account_reference column - ENHANCED
          if (processedData?.bankAccount) {
            processedData.account_reference = processedData?.bankAccount;
            delete processedData?.bankAccount;
          }
          // ADDITIONAL FIX: Handle any potential bank_account variations
          if (processedData?.bank_account) {
            processedData.account_reference = processedData?.bank_account;
            delete processedData?.bank_account;
          }
          if (processedData?.memo) {
            processedData.description = processedData?.memo;
            delete processedData?.memo;
          }
          if (processedData?.paymentMethod) {
            processedData.payment_method = processedData?.paymentMethod;
            delete processedData?.paymentMethod;
          }
          
          // CRITICAL: Remove any remaining references to bankAccount to prevent schema errors
          delete processedData?.bankAccount;
          delete processedData?.bank_account;
          
          // Add organization and user context
          processedData.organization_id = organizationId;
          processedData.created_by = userId;
          processedData.currency_code = processedData?.currency_code || 'USD';
          processedData.status = processedData?.status || 'pending';
          
          // CRITICAL FIX: Remove 'type' field as it doesn't exist in payments_made table schema
          delete processedData?.type;
          
          // CRITICAL FIX: Remove explicit 'id' field that was causing the constraint violation
          delete processedData?.id;
          
          // VALIDATION: Ensure only valid payment_made columns are present
          const validPaymentMadeFields = [
            'account_reference', 'amount_paid', 'bank_name', 'bill_id', 'cleared_by', 
            'cleared_date', 'created_by', 'currency_code', 'description', 'notes', 
            'organization_id', 'payment_date', 'payment_method', 'payment_reference', 
            'reference_number', 'status', 'vendor_id'
            // CRITICAL: Removed 'id' from this list - let database auto-generate it
          ];
          
          // Filter processedData to only include valid columns
          const cleanedData = {};
          Object.keys(processedData)?.forEach(key => {
            if (validPaymentMadeFields?.includes(key)) {
              cleanedData[key] = processedData?.[key];
            }
          });
          
          processedData = cleanedData;
        } else if (type === 'vendor-credit') {
          // CRITICAL FIX: Proper field mapping for vendor credits to match database schema
          if (processedData?.vendor) {
            if (typeof processedData?.vendor === 'object' && processedData?.vendor?.id) {
              processedData.vendor_id = processedData?.vendor?.id;
            } else if (typeof processedData?.vendor === 'string') {
              processedData.vendor_id = processedData?.vendor;
            }
            delete processedData?.vendor;
          }
          
          // CRITICAL FIX: Map 'amount' to 'total_credit_amount' as expected by database
          if (processedData?.amount) {
            processedData.total_credit_amount = parseFloat(processedData?.amount) || 0;
            delete processedData?.amount; // Remove the original amount field
          }
          
          // CRITICAL FIX: Map frontend date field to database column
          if (processedData?.creditDate) {
            processedData.issue_date = processedData?.creditDate; // Use issue_date instead of credit_date
            delete processedData?.creditDate;
          }
          
          // CRITICAL FIX: Map frontend credit number to database column
          if (processedData?.creditNumber) {
            processedData.credit_number = processedData?.creditNumber;
            delete processedData?.creditNumber;
          }
          
          // CRITICAL FIX: Map frontend reason to credit_reason enum
          if (processedData?.reason) {
            processedData.credit_reason = processedData?.reason;
            delete processedData?.reason;
          }
          
          // CRITICAL FIX: Map additional frontend fields to database columns
          if (processedData?.referenceBill) {
            processedData.reference_bill = processedData?.referenceBill;
            delete processedData?.referenceBill;
          }
          
          if (processedData?.referenceNumber) {
            processedData.reference_number = processedData?.referenceNumber;
            delete processedData?.referenceNumber;
          }
          
          if (processedData?.applyTo) {
            processedData.applied_to_type = processedData?.applyTo;
            delete processedData?.applyTo;
          }
          
          if (processedData?.description) {
            processedData.reason_description = processedData?.description;
            delete processedData?.description; // Keep notes separate from reason_description
          }
          
          // Add organization and user context
          processedData.organization_id = organizationId;
          processedData.created_by = userId;
          processedData.currency_code = processedData?.currency_code || 'USD';
          processedData.status = processedData?.status || 'draft';
          
          // VALIDATION: Ensure required fields are present
          if (!processedData?.vendor_id) {
            throw new Error('Please select a vendor for the credit');
          }
          
          if (!processedData?.total_credit_amount || parseFloat(processedData?.total_credit_amount) <= 0) {
            throw new Error('Credit amount must be greater than 0');
          }
          
          if (!processedData?.credit_reason) {
            throw new Error('Please select a reason for the credit');
          }
          
          // CRITICAL: Ensure amount is positive to satisfy the positive_amounts constraint
          processedData.total_credit_amount = Math.abs(parseFloat(processedData?.total_credit_amount)) || 0;
          
          // Set default values for optional amount fields to avoid constraint violations
          processedData.subtotal = processedData?.subtotal ? Math.abs(parseFloat(processedData?.subtotal)) : null;
          processedData.tax_amount = processedData?.tax_amount ? Math.abs(parseFloat(processedData?.tax_amount)) : null;
          processedData.applied_amount = processedData?.applied_amount ? Math.abs(parseFloat(processedData?.applied_amount)) : 0.00;
          
          // CRITICAL: Clean up any remaining frontend-only fields that don't exist in database schema
          delete processedData?.type;
          delete processedData?.id; // Let database auto-generate
        }
      }
      
      console.log('Submitting form data:', processedData);
      
      // Call the save handler
      await onSave(processedData);
      
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error?.message || 'Failed to save. Please check your input and try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (fieldName, fieldConfig) => {
    const value = formData?.[fieldName] || '';

    // Special handling for bill selection field
    if (fieldName === 'bill_selection') {
      return (
        <div key={fieldName} className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {fieldConfig?.label}
            {fieldConfig?.required && <span className="text-destructive ml-1">*</span>}
          </label>
          <select
            value={formData?.bill_id || ''}
            onChange={(e) => handleBillSelection(e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
            disabled={billsLoading}
          >
            {billsLoading ? (
              <option value="">Loading bills...</option>
            ) : (
              billOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))
            )}
          </select>
          {errors?.[fieldName] && (
            <p className="text-sm text-destructive">{errors?.[fieldName]}</p>
          )}
          {billsLoading && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span>Loading available bills...</span>
            </div>
          )}
        </div>
      );
    }

    switch (fieldConfig?.type) {
      case 'select':
        return (
          <Select
            key={fieldName}
            label={fieldConfig?.label}
            required={fieldConfig?.required}
            value={value}
            onValueChange={(newValue) => handleInputChange(fieldName, newValue)}
            options={fieldConfig?.options || []}
            placeholder={`Select ${fieldConfig?.label}`}
            error={errors?.[fieldName]}
          />
        );

      case 'textarea':
        return (
          <div key={fieldName} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {fieldConfig?.label}
              {fieldConfig?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(fieldName, e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder={`Enter ${fieldConfig?.label?.toLowerCase()}`}
            />
            {errors?.[fieldName] && (
              <p className="text-sm text-destructive">{errors?.[fieldName]}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldName} className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={fieldName}
              checked={value || false}
              onChange={(e) => handleInputChange(fieldName, e?.target?.checked)}
              className="rounded border-border"
            />
            <label htmlFor={fieldName} className="text-sm font-medium text-foreground">
              {fieldConfig?.label}
            </label>
          </div>
        );

      default:
        return (
          <Input
            key={fieldName}
            label={fieldConfig?.label}
            type={fieldConfig?.type}
            value={value}
            onChange={(e) => handleInputChange(fieldName, e?.target?.value)}
            error={errors?.[fieldName]}
            required={fieldConfig?.required}
            placeholder={
              fieldName === 'bill_number' && type === 'bills' ?'Enter bill number (e.g., BILL-2024-001)'
                : `Enter ${fieldConfig?.label?.toLowerCase()}`
            }
            min={fieldConfig?.type === 'number' ? '0' : undefined}
            step={fieldConfig?.type === 'number' ? '0.01' : undefined}
            readOnly={fieldConfig?.readonly}
            disabled={fieldConfig?.readonly}
          />
        );
    }
  };

  // Enhanced Line Items Rendering for Bills
  const renderLineItemsTab = () => {
    if (type !== 'bills') return null;

    return (
      <div className="space-y-6">
        {/* Line Items Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-foreground">Line Items</h3>
            <p className="text-sm text-muted-foreground">
              Add items and services to this bill with detailed pricing and tax information
            </p>
          </div>
          <Button onClick={addLineItem} className="flex items-center space-x-2">
            <Icon name="Plus" size={16} />
            <span>Add Line Item</span>
          </Button>
        </div>

        {/* Items Loading State */}
        {itemsLoading && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="Loader2" size={16} className="text-blue-500 animate-spin" />
              <span className="text-blue-700 text-sm">Loading items...</span>
            </div>
          </div>
        )}

        {/* Error Display for Line Items */}
        {errors?.lineItems && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{errors?.lineItems}</p>
          </div>
        )}

        {/* Line Items Table */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Item/Description
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Qty
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tax %
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Line Total
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {lineItems?.map((lineItem, index) => (
                  <tr key={lineItem?.id || index}>
                    {/* Line Number */}
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-foreground">
                      {lineItem?.line_number}
                    </td>
                    
                    {/* Item/Description */}
                    <td className="px-3 py-4">
                      <div className="space-y-2">
                        {/* Item Selector */}
                        <Select
                          value={lineItem?.item_id || ''}
                          onValueChange={(value) => updateLineItem(index, 'item_id', value)}
                          options={itemOptions}
                          placeholder={itemsLoading ? "Loading items..." : "Select item..."}
                          className="w-full min-w-[200px]"
                          disabled={itemsLoading}
                        />
                        {/* Description Field */}
                        <Input
                          value={lineItem?.item_description || ''}
                          onChange={(e) => updateLineItem(index, 'item_description', e?.target?.value)}
                          placeholder="Enter description..."
                          error={errors?.[`lineItem_${index}_description`]}
                          className="w-full"
                        />
                      </div>
                    </td>
                    
                    {/* Account */}
                    <td className="px-3 py-4">
                      <Select
                        value={lineItem?.account_id || ''}
                        onValueChange={(value) => updateLineItem(index, 'account_id', value)}
                        options={accountOptions}
                        placeholder="Select account..."
                        error={errors?.[`lineItem_${index}_account`]}
                        className="w-full min-w-[160px]"
                      />
                    </td>
                    
                    {/* Quantity */}
                    <td className="px-3 py-4">
                      <Input
                        type="number"
                        value={lineItem?.quantity || ''}
                        onChange={(e) => updateLineItem(index, 'quantity', e?.target?.value)}
                        min="0"
                        step="0.001"
                        error={errors?.[`lineItem_${index}_quantity`]}
                        className="w-20"
                      />
                    </td>
                    
                    {/* Unit Price */}
                    <td className="px-3 py-4">
                      <Input
                        type="number"
                        value={lineItem?.unit_price || ''}
                        onChange={(e) => updateLineItem(index, 'unit_price', e?.target?.value)}
                        min="0"
                        step="0.01"
                        error={errors?.[`lineItem_${index}_unit_price`]}
                        className="w-24"
                      />
                    </td>
                    
                    {/* Tax Rate */}
                    <td className="px-3 py-4">
                      <Input
                        type="number"
                        value={lineItem?.tax_rate || ''}
                        onChange={(e) => updateLineItem(index, 'tax_rate', e?.target?.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-20"
                      />
                    </td>
                    
                    {/* Line Total */}
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-foreground font-medium">
                      ${(lineItem?.line_total || 0)?.toFixed(2)}
                    </td>
                    
                    {/* Actions */}
                    <td className="px-3 py-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Empty State */}
                {(!lineItems || lineItems?.length === 0) && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center">
                      <div className="text-center">
                        <Icon name="Package" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-foreground mb-2">No Line Items</h3>
                        <p className="text-muted-foreground mb-4">
                          Add items and services to build your bill
                        </p>
                        <Button onClick={addLineItem}>
                          <Icon name="Plus" size={16} className="mr-2" />
                          Add First Line Item
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bill Totals Summary */}
        {lineItems?.length > 0 && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="max-w-sm ml-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium">${(parseFloat(formData?.subtotal) || 0)?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax Amount:</span>
                <span className="font-medium">${(parseFloat(formData?.tax_amount) || 0)?.toFixed(2)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-semibold">
                <span>Total Amount:</span>
                <span>${(parseFloat(formData?.total_amount) || 0)?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Additional Line Item Options */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Tax Level"
              type="select"
              value={formData?.tax_level || 'transaction_level'}
              onChange={(e) => handleInputChange('tax_level', e?.target?.value)}
              options={[
                { value: 'line_item_level', label: 'Line Item Level' },
                { value: 'transaction_level', label: 'Transaction Level' }
              ]}
            />
            <Input
              label="Tax Type"
              type="select"
              value={formData?.tax_type || 'tax_exclusive'}
              onChange={(e) => handleInputChange('tax_type', e?.target?.value)}
              options={[
                { value: 'tax_exclusive', label: 'Tax Exclusive' },
                { value: 'tax_inclusive', label: 'Tax Inclusive' }
              ]}
            />
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Selected Bill Information Display
  const renderSelectedBillInfo = () => {
    if (type !== 'payment-made' || !selectedBillData) return null;

    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-blue-900">Selected Bill Information</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleBillSelection('')}
            className="text-blue-600 hover:text-blue-700"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-blue-600 font-medium">Bill Number:</span>
            <p className="text-blue-800">{selectedBillData?.bill_number}</p>
          </div>
          <div>
            <span className="text-blue-600 font-medium">Vendor:</span>
            <p className="text-blue-800">{selectedBillData?.vendor_name}</p>
          </div>
          <div>
            <span className="text-blue-600 font-medium">Total Amount:</span>
            <p className="text-blue-800">${selectedBillData?.total_amount?.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-blue-600 font-medium">Balance Due:</span>
            <p className="text-blue-800 font-semibold">${selectedBillData?.balance_due?.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-blue-600 font-medium">Due Date:</span>
            <p className="text-blue-800">{new Date(selectedBillData?.due_date)?.toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-blue-600 font-medium">Issue Date:</span>
            <p className="text-blue-800">{new Date(selectedBillData?.issue_date)?.toLocaleDateString()}</p>
          </div>
        </div>
        {selectedBillData?.amount_paid > 0 && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-800 text-xs">
              <Icon name="Info" size={12} className="inline mr-1" />
              This bill has previous payments of ${selectedBillData?.amount_paid?.toLocaleString()}
            </p>
          </div>
        )}
      </div>
    );
  };

  const getFieldsForTab = (tabId) => {
    const fieldKeys = Object.keys(config?.fields);
    
    switch (tabId) {
      case 'basic':
        if (type === 'vendors') {
          return ['vendor_name', 'email', 'phone', 'company_name'];
        } else if (type === 'expenses') {
          return ['vendor', 'description', 'amount', 'expenseDate'];
        } else if (type === 'recurring-expenses') {
          return ['template_name', 'vendor_id', 'description', 'amount'];
        } else if (type === 'bills') {
          return ['vendor_id', 'bill_number', 'issue_date', 'due_date', 'status'];
        } else if (type === 'recurring-bills') {
          return ['vendor_id', 'bill_description', 'total_amount', 'recurring_frequency'];
        } else if (type === 'payment-made') {
          return ['bill_selection', 'vendor', 'amount', 'paymentDate', 'paymentMethod'];
        } else if (type === 'vendor-credit') {
          return ['vendor', 'creditNumber', 'creditDate', 'amount', 'reason'];
        }
        break;
      
      case 'contact':
        return ['website', 'address_line_1', 'address_line_2', 'city', 'state_province', 'postal_code', 'country'];
      
      case 'payment':
        if (type === 'bills') {
          return ['reference_number', 'subtotal', 'tax_amount', 'total_amount', 'payment_terms', 'notes'];
        } else {
          return ['tax_id', 'payment_terms', 'credit_limit', 'currency_code', 'status', 'notes'];
        }
      
      case 'categorization':
        return ['account_id', 'paymentMethod', 'taxAmount', 'billable', 'receiptAttached'];
      
      case 'receipt':
        return ['reference', 'notes'];
      
      case 'items':
        // This will be handled by renderLineItemsTab for bills
        return [];
      
      case 'schedule':
        if (type === 'recurring-expenses') {
          return ['recurring_frequency', 'start_date', 'end_date', 'next_expense_date'];
        } else if (type === 'recurring-bills') {
          return ['issue_date', 'due_date', 'next_billing_date', 'recurring_end_date', 'payment_terms', 'reference_number', 'subtotal', 'tax_amount', 'notes'];
        }
        break;
      
      case 'settings':
        if (type === 'recurring-expenses') {
          return ['account_id', 'payment_method', 'tax_amount', 'currency_code', 'exchange_rate', 'auto_approve', 'is_billable', 'send_notification', 'reference_number', 'notes'];
        }
        break;
      
      case 'allocation':
        if (type === 'payment-made') {
          return ['referenceNumber', 'billNumber', 'bankAccount', 'memo', 'notes'];
        }
        break;
      
      case 'reason':
        if (type === 'vendor-credit') {
          return ['referenceBill', 'referenceNumber', 'applyTo', 'description', 'notes'];
        }
        break;
      
      default:
        return fieldKeys?.slice(0, 4);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {config?.title}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {config?.tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Render Selected Bill Info for payment-made */}
            {renderSelectedBillInfo()}
            
            {/* Render Line Items Tab for Bills */}
            {activeTab === 'items' && (type === 'bills' || type === 'recurring-bills') ? (
              renderLineItemsTab()
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFieldsForTab(activeTab)?.map((fieldName) => (
                  renderField(fieldName, config?.fields?.[fieldName])
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Footer with loading and error states */}
          <div className="flex flex-col space-y-3 p-6 border-t border-border bg-muted/20">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-red-500" />
                  <span className="text-red-700 text-sm font-medium">Error</span>
                </div>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setError(null)}
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            )}
            
            <div className="flex items-center justify-end space-x-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="default"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    {item ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} className="mr-2" />
                    {item ? `Update ${config?.title?.split(' ')?.[1]}` : `Create ${config?.title?.split(' ')?.[2] || config?.title?.split(' ')?.[1]}`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseFormModal;